import { db } from './db';
import { PendingReceipt } from '../models/types';

/**
 * The receipt queue.
 *
 * Capture writes to IndexedDB FIRST and reports success there; delivery
 * happens afterwards and retries until the dashboard confirms it. Receipts are
 * created at lumberyards, gas stations and job sites — exactly where signal is
 * worst — so failing at the moment of capture would fail at the moment of
 * highest value, and would put the receipt straight back in the truck console
 * this feature exists to empty.
 *
 * This replaces the 2026-07-21 branch's receiptSyncService.ts entirely. That
 * module posted browser-direct to a user-entered dashboard URL with a
 * user-entered SYNC_TOKEN; Cloudflare Access removed both. Posting is now
 * same-origin and functions/api/sync/[[path]].ts attaches the credentials.
 */

const RECEIPTS_PATH = '/api/sync/receipts';

export interface QueuedReceiptInput {
  image: Blob;
  vendor: string;
  amountCents: number;
  txnDate: string;
  jobRemoteId?: number;
  jobName?: string;
  category: string;
  paymentMethod: string;
  billable: boolean;
  notes?: string;
}

/** Guards against a drain triggered from two places at once (app open + save). */
let draining = false;

export const enqueueReceipt = async (input: QueuedReceiptInput): Promise<string> => {
  const clientUuid =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;

  await db.pendingReceipts.add({
    ...input,
    clientUuid,
    createdAt: new Date(),
    attempts: 0,
    // Explicit 0, not omitted. needsAttention is an INDEXED Dexie field
    // ('++id, clientUuid, createdAt, needsAttention' in db.ts), and
    // IndexedDB drops a record from an index entirely when its key path is
    // undefined — an omitted needsAttention here would make the receipt
    // invisible to where('needsAttention').equals(0), which is exactly the
    // query Task 10's "Pending (N)" view runs. It also keeps this file's two
    // writers of the field (this one and retryReceipt below) agreeing on a
    // single representation instead of "undefined here, 0 there".
    needsAttention: 0,
  });

  return clientUuid;
};

export const getPendingReceiptCount = async (): Promise<number> => db.pendingReceipts.count();

/**
 * A 4xx will not fix itself on a retry, so looping on one burns battery and —
 * worse — makes a permanently stuck receipt look identical to "still offline"
 * in the pending count. 403 is the exception: it means this device is not
 * enrolled yet, which the employee CAN fix, so it stays retryable.
 */
const isRetryable = (status: number): boolean =>
  status >= 500 || status === 408 || status === 429 || status === 403;

const buildForm = (receipt: PendingReceipt): FormData => {
  const form = new FormData();

  // A File (not a bare Blob) so the endpoint's `image instanceof File` check
  // passes and the filename/type reach R2.
  form.append('image', new File([receipt.image], 'receipt.jpg', { type: receipt.image.type || 'image/jpeg' }));
  form.append('vendor', receipt.vendor);
  form.append('amount', (receipt.amountCents / 100).toFixed(2));
  form.append('txnDate', receipt.txnDate);
  form.append('category', receipt.category);
  form.append('paymentMethod', receipt.paymentMethod);
  form.append('billable', String(receipt.billable));
  form.append('clientUuid', receipt.clientUuid);

  // Send both when both are known — not XOR. The server's resolveJobForReceipt
  // tries jobId first and falls through to jobName only on a miss (e.g. the
  // job was hard-deleted on the dashboard after this device last synced its
  // catalog, so a stale remoteId no longer resolves). That fall-through is
  // dead code unless the name travels alongside the id (review finding: a
  // receipt for a since-deleted job silently lands with job_id NULL,
  // otherwise). The name is still sent alone for a job this device created
  // locally that the dashboard has never seen.
  if (receipt.jobRemoteId) form.append('jobId', String(receipt.jobRemoteId));
  if (receipt.jobName) form.append('jobName', receipt.jobName);

  if (receipt.notes) form.append('notes', receipt.notes);

  return form;
};

export interface SyncResult {
  delivered: number;
  failed: number;
  /**
   * Set when the drain did not actually attempt anything, distinguishing two
   * callers can't otherwise tell apart from a bare {delivered:0, failed:0}:
   * a UI showing "0 delivered, 0 failed" for that reason must not read as
   * "tried and there was nothing to do" (review finding, Task 10 round 2).
   * - 'reentrant': another drain was already in flight (the `draining` guard).
   * - 'unenrolled': this device has no enrollment code yet.
   * Absent (undefined, not a third enum value) when a drain genuinely ran,
   * even if it happened to touch zero rows.
   */
  skipped?: 'reentrant' | 'unenrolled';
  /**
   * Count of rows this drain skipped over because needsAttention was already
   * 1 — present and non-zero only when at least one was skipped. Lets a
   * caller (PendingReceipts' describeResult) tell "a drain ran and there was
   * truly nothing left" apart from "a drain ran but every row is flagged and
   * needs a per-row Retry" — both are {delivered:0, failed:0} otherwise
   * (review finding: the footer button went silent in the all-stuck case).
   */
  stuck?: number;
}

export const syncPendingReceipts = async (): Promise<SyncResult> => {
  if (draining) return { delivered: 0, failed: 0, skipped: 'reentrant' };
  draining = true;

  let delivered = 0;
  let failed = 0;
  let stuck = 0;

  try {
    const code = await db.getEnrollmentCode();

    // An unenrolled device is a guaranteed 403 on every single row — the
    // endpoint checks enrollment before it even reads the multipart body
    // (server-contract fact #5). Draining anyway would re-upload every
    // queued image in full, on every call, purely to relearn what is already
    // known here for free. The concrete worst case this guards against: an
    // employee with several queued job-site photos, still not enrolled,
    // burning cell data resending them on every app open.
    if (!code) {
      return { delivered: 0, failed: 0, skipped: 'unenrolled' };
    }

    const rows = await db.pendingReceipts.toArray();

    for (const receipt of rows) {
      if (receipt.needsAttention === 1) {
        stuck++;
        continue;
      }

      try {
        // NO Content-Type header. The browser must set it, because only the
        // browser knows the multipart boundary it generated for this FormData.
        // syncService.ts's getAuthHeaders() hardcodes application/json and
        // would silently corrupt the body — do not reuse it here.
        const headers: Record<string, string> = { 'x-device-code': code };

        const response = await fetch(RECEIPTS_PATH, {
          method: 'POST',
          headers,
          body: buildForm(receipt),
        });

        // 201 = inserted, 200 = the dashboard already had it (a replay of a
        // confirmation we never received). Both mean it is safe to drop.
        if (response.ok) {
          await db.pendingReceipts.delete(receipt.id!);
          delivered++;
          continue;
        }

        failed++;
        const detail = await response.text().catch(() => '');
        const lastError = `HTTP ${response.status}${detail ? `: ${detail.slice(0, 200)}` : ''}`;

        await db.pendingReceipts.update(receipt.id!, {
          attempts: (receipt.attempts || 0) + 1,
          lastError,
          ...(isRetryable(response.status) ? {} : { needsAttention: 1 }),
        });
      } catch (error) {
        failed++;
        await db.pendingReceipts.update(receipt.id!, {
          attempts: (receipt.attempts || 0) + 1,
          lastError: error instanceof Error ? error.message : 'Upload failed',
        });
      }
    }
  } finally {
    draining = false;
  }

  return { delivered, failed, ...(stuck > 0 ? { stuck } : {}) };
};

/** Clear the stuck flag so the employee can retry after the office fixes something. */
export const retryReceipt = async (id: number): Promise<void> => {
  await db.pendingReceipts.update(id, { needsAttention: 0, lastError: undefined });
};

export const discardReceipt = async (id: number): Promise<void> => {
  await db.pendingReceipts.delete(id);
};
